import React from "react";
import { Zap, Fingerprint, Search, FileSignature, Upload, MapPinned, FileCheck2 } from "lucide-react";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { PageHero, SectionHeading, FeatureCard, CtaBanner } from "@/components/marketing/blocks";
import { PricingSection } from "@/components/marketing/PricingSection";
import { useSeo } from "@/hooks/useSeo";

const CAPABILITIES = [
  { icon: Zap, title: "Rush & 24-Hour Priority Service", desc: "Standard, rush, and 24-Hour Priority options when a deadline is approaching." },
  { icon: Fingerprint, title: "GPS-Timestamped Attempts", desc: "Every attempt captures location and time so your record is clear and verifiable." },
  { icon: Search, title: "Skip Tracing Support", desc: "Locating hard-to-find recipients so your documents reach the right person." },
  { icon: FileSignature, title: "Affidavits of Service", desc: "Detailed, signed affidavits of service prepared for filing with the court." },
];

const STEPS = [
  { icon: Upload, title: "1. Submit Your Documents", desc: "Create a service request and upload the papers that need to be served." },
  { icon: MapPinned, title: "2. We Attempt & Track", desc: "Our servers make attempts in the field, logging GPS, time, and photos as they go." },
  { icon: FileCheck2, title: "3. Receive Your Affidavit", desc: "Once served, you receive a court-ready affidavit of service with the full attempt log." },
];

export default function ProcessServing() {
  const origin = "https://artadmin.net";
  useSeo({
    title: "Process Serving in Memphis & Shelby County, TN | AR Process Service and Notary",
    description:
      "Professional process serving in Memphis and Shelby County, TN. GPS-verified attempts, rush and 24-Hour Priority service, skip tracing, and court-ready affidavits of service.",
    canonicalPath: "/process-serving",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Process Serving",
      provider: { "@type": "LegalService", "@id": "https://artadmin.net/#business", name: "AR Process Service and Notary" },
      areaServed: "Memphis Metro, Shelby County, Tennessee",
      url: `${origin}/process-serving`,
    },
  });

  return (
    <MarketingLayout>
      <PageHero
        overline="Process Serving"
        title="Professional Process Serving"
        subtitle="Fast, accurate service of legal documents backed by GPS-verified attempts and detailed affidavits — so your proof of service holds up when it matters."
      />

      <section className="px-5 py-14 sm:px-8 lg:py-20">
        <SectionHeading overline="Capabilities" title="Built for accuracy and accountability" />
        <div className="mx-auto mt-10 grid max-w-5xl gap-6 sm:grid-cols-2">
          {CAPABILITIES.map((c) => (
            <FeatureCard key={c.title} icon={c.icon} title={c.title}>
              {c.desc}
            </FeatureCard>
          ))}
        </div>
      </section>

      <section className="px-5 py-14 sm:px-8 lg:py-20" data-testid="what-we-serve-section">
        <SectionHeading
          title="What We Serve"
          subtitle="We serve summonses, complaints, subpoenas, restraining orders, divorce papers, eviction notices, and all other legal documents."
        />
      </section>

      <section className="border-y border-slate-200 bg-white px-5 py-14 sm:px-8 lg:py-20" data-testid="ps-details">
        <div className="mx-auto max-w-5xl space-y-10">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">Courts we serve</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-600 sm:text-base">
              We serve documents for matters in the Shelby County General Sessions, Circuit, Chancery, Juvenile, and Probate Courts.
            </p>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">Documents we serve</h2>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-[15px] leading-relaxed text-slate-600 sm:text-base">
              <li>Summons and complaints</li>
              <li>Subpoenas</li>
              <li>Eviction notices and detainer warrants</li>
              <li>Divorce and family court papers</li>
              <li>Small claims</li>
            </ul>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">Speed options</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-600 sm:text-base">
              Choose the pace your deadline needs: Routine ($95), Rush ($135), or same-day / 24-Hour Priority ($285) service, at our existing prices.
            </p>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">Hard-to-serve defendants</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-600 sm:text-base">
              For evasive or hard-to-find defendants, we make multiple attempts at different times of day, plus skip tracing to help locate the right person.
            </p>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">Out-of-state attorneys and law firms</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-600 sm:text-base">
              We serve Tennessee papers for firms anywhere and return a notarized affidavit of service with GPS-stamped, timestamped proof.
            </p>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">Who we serve</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-600 sm:text-base">
              We work with law firms, paralegals, landlords and property managers, and individuals.
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white px-5 py-14 sm:px-8 lg:py-20">
        <SectionHeading overline="How it works" title="Three simple steps" />
        <div className="mx-auto mt-10 grid max-w-5xl gap-6 lg:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-7">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900">
                <s.icon className="h-6 w-6 text-sky-400" />
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900 sm:text-xl">{s.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600 sm:text-base">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <PricingSection disclaimer={false} />

      <CtaBanner title="Have documents to serve?" subtitle="Start a service request and put an audit-ready process behind every attempt." />

      <PricingSection cards={false} />
    </MarketingLayout>
  );
}
